import localClipboard from './utils';

export const ToolbarSeparator = '|';
const TEXT_NODE = 3;
const INDENT_CHARACTER = '\t';

export const OPTIONS_ID = {
  BOLD: 'bold',
  ITALIC: 'italic',
  UNDERLINE: 'underline',
  SPECIAL_CHARACTERS: 'special_characters',
  SPECIAL_MATH_CHARACTERS: 'special_math_characters',
  CUT: 'cut',
  COPY: 'copy',
  PASTE: 'paste',
  UNDO: 'undo',
  REDO: 'redo',
  SUPERSCRIPT: 'superscript',
  SUBSCRIPT: 'subscript',
  INDENT: 'indent',
  IMAGE_UPLOAD: 'image_upload',
};

const SpecialCharactersHandler = () => {
  const specialCharacterTriggers = Array.from(
    document.querySelectorAll('[title^="Special characters"]'),
  );

  const specialCharacterPanels = document.querySelectorAll(
    '.lrn_widget.lrn_charactermap_wrapper',
  );

  const windowHeight = window.innerHeight;
  specialCharacterPanels.forEach((panel, panelIndex) => {
    if (panel.firstChild) {
      // is open
      const trigger = specialCharacterTriggers[panelIndex];
      const { x, y } = trigger.getBoundingClientRect();

      const charactersPanelHeight = panel.firstChild.offsetHeight;

      panel.style.setProperty('left', `${x - 75}px`);
      if (charactersPanelHeight + y > windowHeight) {
        // we have to display the panel above the toolbar
        panel.style.setProperty('top', `${y - 10 - charactersPanelHeight}px`);
      } else {
        panel.style.setProperty('top', `${y + 25}px`);
      }
    }
  });
};

const Bold = {
  id: OPTIONS_ID.BOLD,
  icon: 'bold',
  tooltip: 'Bold (Ctrl+B)',
  classSelector: '.lrn-editor-toolbar-button-bold',
};

const Italic = {
  id: OPTIONS_ID.ITALIC,
  icon: 'italic',
  tooltip: 'Italic (Ctrl+I)',
  classSelector: '.lrn-editor-toolbar-button-italic',
};

const Underline = {
  id: OPTIONS_ID.UNDERLINE,
  icon: 'underline',
  tooltip: 'Underline (Ctrl+U)',
  classSelector: '.lrn-editor-toolbar-button-underline',
};

const SpecialLanguageCharacters = {
  id: OPTIONS_ID.SPECIAL_CHARACTERS,
  textIcon: 'á',
  tooltip: 'Special characters',
  classSelector: 'button[title="Special Characters"]',
  customHandler: SpecialCharactersHandler,
};

const SpecialMathCharacters = {
  id: OPTIONS_ID.SPECIAL_MATH_CHARACTERS,
  icon: 'special_characters',
  tooltip: 'Special characters',
  classSelector: 'button[title=""]',
  hasDropdown: true,
  customHandler: SpecialCharactersHandler,
};

const Cut = {
  id: OPTIONS_ID.CUT,
  icon: 'cut',
  tooltip: 'Cut (Ctrl+X)',
  customHandler: (event, element, isSecure) => {
    const selection = window.getSelection();
    if (!selection) return;

    const selectedText = selection.toString();
    if (!selectedText) return;

    const mainEditorDiv = element.querySelector(
      'div.lrn_texteditor_editable.lrn_editor_area.lrn_longtextinput.wysihtml-sandbox.wysihtml-editor',
    );
    if (!mainEditorDiv) return;

    const currentSelectionsParent = mainEditorDiv.querySelectorAll('p, div');
    const isSelectionFound =
      Array.from(currentSelectionsParent).some(
        (currentSelectionParent) =>
          currentSelectionParent.textContent.trim() === selectedText,
      ) || mainEditorDiv.contains(selection.anchorNode);

    if (isSelectionFound) {
      const isInsideEditor = Array.from(
        document.querySelectorAll('.lrn_texteditor_editable'),
      ).some((editor) => editor.contains(selection.focusNode));

      if (isInsideEditor) {
        if (isSecure) {
          localClipboard.writeText(selection);
        } else {
          navigator.clipboard.writeText(selectedText);
        }
        selection.deleteFromDocument();
      }
    }
  },
};

const Copy = {
  id: OPTIONS_ID.COPY,
  icon: 'copy',
  tooltip: 'Copy (Ctrl+C)',
  customHandler: (e, element, isSecure) => {
    const selection = window.getSelection().toString();
    if (selection) {
      if (isSecure) {
        localClipboard.writeText(selection);
      } else {
        navigator.clipboard.writeText(selection);
      }
    }
  },
};

const Paste = {
  id: OPTIONS_ID.PASTE,
  icon: 'paste',
  tooltip: 'Paste (Ctrl+V)',
  customHandler: (e, element, isSecure) => {
    const selection = window.getSelection();

    const insertTextInSelection = (text) => {
      if (selection.getRangeAt && selection.rangeCount) {
        const range = window.getSelection().getRangeAt(0);
        range.collapse(false);
        const el = document.createElement('div');
        el.innerHTML = text;
        const frag = document.createDocumentFragment();
        let node;
        while ((node = el.firstChild)) {
          frag.appendChild(node);
        }
        range.insertNode(frag);
      }
    };

    if (selection) {
      const isInsideEditor = Array.from(
        document.querySelectorAll('.lrn_texteditor_editable'),
      ).some((editor) => editor.contains(selection.focusNode));

      if (isInsideEditor) {
        if (isSecure) {
          const localClipboardText = localClipboard.readText();
          insertTextInSelection(localClipboardText);
        } else {
          navigator.clipboard.readText().then((clipboardText) => {
            insertTextInSelection(clipboardText);
          });
        }
      }
    }
  },
};

const Undo = {
  id: OPTIONS_ID.UNDO,
  icon: 'undo',
  tooltip: 'Undo (Ctrl+Z)',
  classSelector: '.lrn-editor-toolbar-button-undo',
  calculateIsDisabled: (editorTouched) => !editorTouched,
};

const Redo = {
  id: OPTIONS_ID.REDO,
  icon: 'redo',
  tooltip: 'Redo (Ctrl+Y)',
  classSelector: '.lrn-editor-toolbar-button-redo',
  calculateIsDisabled: (editorTouched) => !editorTouched,
};

const Superscript = {
  id: OPTIONS_ID.SUPERSCRIPT,
  icon: 'superscript',
  tooltip: 'Superscript',
  classSelector: '.lrn-editor-toolbar-button-superscript',
};

const Subscript = {
  id: OPTIONS_ID.SUBSCRIPT,
  icon: 'subscript',
  tooltip: 'Subscript',
  classSelector: '.lrn-editor-toolbar-button-subscript',
};

const Indent = {
  id: OPTIONS_ID.INDENT,
  icon: 'indent',
  tooltip: 'Indent',
  customHandler: (event, element) => {
    const selection = window.getSelection();
    const currentSelectionsParent = element.querySelectorAll(
      'div.lrn_texteditor_editable.lrn_editor_area.lrn_longtextinput.wysihtml-sandbox.wysihtml-editor p',
    );
    const isSelectionFound = Array.from(currentSelectionsParent).some(
      (currentSelectionParent) => {
        const sanitizedInnerHTML = currentSelectionParent.innerHTML.replace(
          /<br>/g,
          '',
        );
        return (
          selection.anchorNode.parentNode.textContent === sanitizedInnerHTML
        );
      },
    );
    if (selection && isSelectionFound) {
      if (selection.rangeCount) {
        const range = selection.getRangeAt(0);
        const ancestor = range.commonAncestorContainer;
        if (ancestor instanceof HTMLParagraphElement) {
          // the selection is an empty <p>
          ancestor.prepend(document.createTextNode(INDENT_CHARACTER));
          selection.removeAllRanges();
          const newRange = document.createRange();
          newRange.setStart(ancestor, 1);
          newRange.collapse(true);
          selection.addRange(newRange);
        }
        // the selection is one line of text
        if (ancestor.nodeType === TEXT_NODE) {
          ancestor.parentNode.prepend(
            document.createTextNode(INDENT_CHARACTER),
          );
        } else {
          // the selection has multiple lines
          Array.from(ancestor.querySelectorAll('p'))
            .filter((line) => selection.containsNode(line, true))
            .forEach((line) => {
              line.prepend(document.createTextNode(INDENT_CHARACTER));
            });
        }
      }
    }
  },
};

const ImageUpload = {
  id: OPTIONS_ID.IMAGE_UPLOAD,
  icon: 'image_upload',
  tooltip: 'Image Upload',
  classSelector: '.lrn-editor-toolbar-button-insertImage',
};

export const FORMATTING_OPTIONS = {
  bold: Bold,
  italic: Italic,
  underline: Underline,
  characterMathMap: SpecialMathCharacters,
  cut: Cut,
  copy: Copy,
  paste: Paste,
  superscript: Superscript,
  undo: Undo,
  redo: Redo,
  subscript: Subscript,
  charactermap: SpecialLanguageCharacters,
  indent: Indent,
  image: ImageUpload,
};

export const BLACKLIST = [
  'unorderedList',
  'orderedList',
  'codeInline',
  'codeEditor',
];
export const WHITELIST = ['copy', 'cut', 'paste', 'indent'];

const defaultFormattingOptions = [
  'bold',
  'italic',
  'underline',
  ToolbarSeparator,
  'characterMathMap',
  ToolbarSeparator,
  'cut',
  'copy',
  'paste',
  ToolbarSeparator,
  'undo',
  'redo',
  ToolbarSeparator,
  'superscript',
  'subscript',
  ToolbarSeparator,
  'indent',
  'image',
];

export const getOrderedAndGroupedFormattingOptions = (formattingOptions) => {
  //filter out unused options
  const res = defaultFormattingOptions.filter(
    (option) =>
      formattingOptions.includes(option) || option === ToolbarSeparator,
  );

  //add the options that are not already in the result, to the end
  res.push(
    ...formattingOptions.filter(
      (option) => !defaultFormattingOptions.includes(option),
    ),
  );

  //remove separator if its last item
  if (res[res.length - 1] === ToolbarSeparator) {
    res.pop();
  }

  //filter out consecutive separators
  return res.filter((option, i) => option !== res[i + 1]);
};
